import {useMemo} from 'react'

import {filterConversationItems} from './utils/filter-items'
import {buildConversationItems, buildReviewThreadItems} from './utils/build-conversation-items'

import {usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import type {CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import type {CommentsDisplayMode} from './utils/display-mode'

export interface UseAuthorFilterItemsOptions {
  filterState: CommentsFilterState
  displayMode: CommentsDisplayMode
}

/** Returns the list of all authors in the conversation with filters applied, excluding any applied author filters. */
export function useAuthorFilterItems({filterState, displayMode}: UseAuthorFilterItemsOptions): AuthorFilterItem[] {
  const threadPreviews = useThreads()

  const basePath = usePullRequestBasePath()

  const conversationComments = useInfiniteConversationCommentsQuery({
    basePath,
    enabled: displayMode === 'conversation',
  }).data?.comments

  const authorList = useMemo(() => {
    const allItems =
      displayMode === 'conversation'
        ? buildConversationItems(conversationComments ?? [], [
            /* child threads are (currently) ignored when building the author list */
          ])
        : buildReviewThreadItems(threadPreviews)

    const authorCountFilterState = {
      ...filterState,
      selectedAuthors: undefined,
    }
    const filteredItems = filterConversationItems(allItems, authorCountFilterState)

    const allAuthors = getConversationItemsAuthors(allItems, filteredItems)

    return filterState.showAgents === false ? allAuthors.filter(author => !author.isAgent) : allAuthors
  }, [filterState, displayMode, conversationComments, threadPreviews])

  return authorList
}
